import React, { useCallback, useEffect, useState } from 'react';
import {
  Card,
  Button,
  InputNumber,
  message,
  Typography,
  Spin,
  Steps,
} from 'antd';
import {
  UploadOutlined,
  CompassOutlined,
  FileSearchOutlined,
  HomeOutlined,
} from '@ant-design/icons';
import ImageUploader from '../components/ImageUploader';
import AnalysisResult from '../components/AnalysisResult';
import BuildingLocationPicker from '../components/BuildingLocationPicker';
import CompassCalibration from '../components/CompassCalibration';
import AirflowVisualization from '../components/AirflowVisualization';
import {
  analyzeAirflow,
  analyzeFengShuiReport,
  analyzeImage,
  defaultPublicFeatureFlags,
  fetchPublicFeatures,
} from '../services/api';
import {
  AirflowAnalysisResult,
  BuildingLocation,
  ImageAnalysisResult,
  FengShuiAnalysisResult,
} from '../types';
import {
  buildHouseLuckChart,
  PalaceKey,
} from '../utils/houseLuckChart';

const { Title, Paragraph } = Typography;

interface ReportSnapshot {
  houseYear: number;
  annualYear: number;
  sitting: PalaceKey;
  facing: PalaceKey;
  sittingAngle: number;
  facingAngle: number;
}

/**
 * 风水分析主页面
 */
const FengShuiAnalyzer: React.FC = () => {
  const [loadingStage, setLoadingStage] = useState<'image' | 'report' | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [imageAnalysis, setImageAnalysis] = useState<ImageAnalysisResult | null>(null);
  const [airflowAnalysis, setAirflowAnalysis] = useState<AirflowAnalysisResult | null>(null);
  const [fengShuiAnalysis, setFengShuiAnalysis] = useState<FengShuiAnalysisResult | null>(null);
  const [reportSnapshot, setReportSnapshot] = useState<ReportSnapshot | null>(null);
  const [houseYear, setHouseYear] = useState(new Date().getFullYear());
  const [buildingLocation, setBuildingLocation] = useState<BuildingLocation | null>(null);
  const [sitting, setSitting] = useState<PalaceKey>('north');
  const [facing, setFacing] = useState<PalaceKey>('south');
  const [sittingAngle, setSittingAngle] = useState(0);
  const [facingAngle, setFacingAngle] = useState(180);
  const [compassRotation, setCompassRotation] = useState(0);
  const [features, setFeatures] = useState(defaultPublicFeatureFlags);

  useEffect(() => {
    let mounted = true;
    const refreshFeatures = () => {
      void fetchPublicFeatures()
        .then(value => { if (mounted) setFeatures(value); })
        .catch(error => console.warn('Feature flags unavailable', error));
    };
    refreshFeatures();
    const timer = window.setInterval(refreshFeatures, 30_000);
    return () => {
      mounted = false;
      window.clearInterval(timer);
    };
  }, []);

  const handleCalibratedSittingFacing = useCallback((value: {
    sitting: PalaceKey;
    facing: PalaceKey;
    sittingAngle: number;
    facingAngle: number;
  }) => {
    setSitting(value.sitting);
    setFacing(value.facing);
    setSittingAngle(value.sittingAngle);
    setFacingAngle(value.facingAngle);
  }, []);

  const handleFileSelect = (file: File, preview: string) => {
    setSelectedFile(file);
    setPreviewUrl(preview);
    // 清空之前的结果
    setImageAnalysis(null);
    setAirflowAnalysis(null);
    setFengShuiAnalysis(null);
    setReportSnapshot(null);
    setCompassRotation(0);
    setSittingAngle(0);
    setFacingAngle(180);
  };

  const handleAnalyze = async () => {
    if (!features.floorplan_recognition) {
      message.warning('户型识别功能维护中');
      return;
    }
    if (!selectedFile) {
      message.warning('请先上传一张户型图');
      return;
    }

    setLoadingStage('image');
    try {
      const result = await analyzeImage(selectedFile, features.environment_analysis ? buildingLocation ?? undefined : undefined);
      setImageAnalysis(result);
      const airflowResult = features.airflow_analysis ? await analyzeAirflow(result) : null;
      setAirflowAnalysis(airflowResult);
      setFengShuiAnalysis(null);
      setReportSnapshot(null);
      message.success('户型识别与气流路径生成完成，请校准罗盘');
    } catch (error) {
      console.error('Analysis error:', error);
      message.error('户型识别失败，请重试');
    } finally {
      setLoadingStage(null);
    }
  };

  const handleGenerateReport = async () => {
    if (!features.ai_report) {
      message.warning('AI 分析报告功能维护中');
      return;
    }
    if (!imageAnalysis) {
      message.warning('请先完成户型识别');
      return;
    }

    setLoadingStage('report');
    setFengShuiAnalysis(null);
    setReportSnapshot(null);
    try {
      const snapshot: ReportSnapshot = {
        houseYear,
        annualYear: new Date().getFullYear(),
        sitting,
        facing,
        sittingAngle,
        facingAngle,
      };
      const chart = buildHouseLuckChart(
        snapshot.houseYear,
        snapshot.annualYear,
        snapshot.sitting,
        snapshot.facing,
        snapshot.sittingAngle,
        snapshot.facingAngle,
      );
      const result = await analyzeFengShuiReport(imageAnalysis, chart, features.environment_analysis ? buildingLocation ?? undefined : undefined);
      setFengShuiAnalysis(result);
      setReportSnapshot(snapshot);
      message.success('分析完成');
    } catch (error) {
      console.error('Report error:', error);
      message.error('风水分析失败，请重试');
    } finally {
      setLoadingStage(null);
    }
  };

  return (
    <div className="analyzer-shell">
      <section className="hero-panel" id="upload">
        <div className="hero-copy">
          <span className="section-kicker">Floor plan diagnostics</span>
          <Title level={1}>把户型图变成可读的阳宅判断</Title>
          <Paragraph>
            上传平面图后，系统会识别房间、入户点、朝向和空间关系，并生成一份更适合落地调整的风水分析。
          </Paragraph>
          <div className="hero-metrics" aria-label="分析流程概览">
            <span>
              图像识别
            </span>
            <span>
              方位校准
            </span>
            <span>
              格局建议
            </span>
          </div>
        </div>

        <Card className="upload-card" bordered={false}>
          <ImageUploader onFileSelect={handleFileSelect} previewUrl={previewUrl} />

          <label className="pre-analysis-field">
            <span>入伙/建造年份</span>
            <InputNumber
              min={1864}
              max={2043}
              value={houseYear}
              onChange={(value) => setHouseYear(typeof value === 'number' ? value : new Date().getFullYear())}
            />
          </label>

          <BuildingLocationPicker
            value={buildingLocation}
            onChange={setBuildingLocation}
            enabled={features.environment_analysis}
          />

          <div className="analyze-actions">
            <Button
              type="primary"
              size="large"
              onClick={handleAnalyze}
              loading={loadingStage === 'image'}
              disabled={!features.floorplan_recognition || !selectedFile || loadingStage === 'report'}
              title={!features.floorplan_recognition ? '管理员已暂停户型识别功能' : undefined}
              icon={<UploadOutlined />}
            >
              {features.floorplan_recognition ? '识别户型' : '户型识别维护中'}
            </Button>
            <span className="file-state">
              {selectedFile ? selectedFile.name : '等待上传户型图'}
            </span>
          </div>
        </Card>
      </section>

      <section className="method-strip" id="method" aria-label="分析步骤">
        <Steps
          responsive
          items={[
            {
              title: '上传户型',
              description: '支持常见图片格式',
              icon: <HomeOutlined />,
            },
            {
              title: '识别空间',
              description: '提取房间与入户点',
              icon: <FileSearchOutlined />,
            },
            {
              title: '校准罗盘',
              description: '拖动调整真实朝向',
              icon: <CompassOutlined />,
            },
          ]}
        />
      </section>

      {imageAnalysis && previewUrl && (
        <section id="result" className="calibration-section">
          <CompassCalibration
            imageAnalysis={imageAnalysis}
            previewUrl={previewUrl}
            rotation={compassRotation}
            onRotationChange={setCompassRotation}
            onSittingFacingChange={handleCalibratedSittingFacing}
          />
          {airflowAnalysis && (
            <AirflowVisualization
              imageAnalysis={imageAnalysis}
              airflowAnalysis={airflowAnalysis}
              previewUrl={previewUrl}
            />
          )}
          <div className="calibration-actions">
            <Button
              type="primary"
              size="large"
              onClick={handleGenerateReport}
              loading={loadingStage === 'report'}
              disabled={!features.ai_report || loadingStage === 'image'}
              title={!features.ai_report ? '管理员已暂停 AI 分析报告功能' : undefined}
            >
              {!features.ai_report ? '报告生成维护中' : fengShuiAnalysis ? '重新生成风水分析' : '生成风水分析'}
            </Button>
          </div>
        </section>
      )}

      {fengShuiAnalysis && imageAnalysis && reportSnapshot && (
        <section id="report" className="report-section">
          <AnalysisResult
            imageAnalysis={imageAnalysis}
            fengShuiAnalysis={fengShuiAnalysis}
            airflowAnalysis={airflowAnalysis ?? undefined}
            previewUrl={previewUrl!}
            houseYear={reportSnapshot.houseYear}
            annualYear={reportSnapshot.annualYear}
            sitting={reportSnapshot.sitting}
            facing={reportSnapshot.facing}
            sittingAngle={reportSnapshot.sittingAngle}
            facingAngle={reportSnapshot.facingAngle}
          />
        </section>
      )}

      {loadingStage && (
        <Card className="loading-card" bordered={false}>
          <Spin size="large" />
          {loadingStage === 'image' ? (
            <div>
              <strong>正在识别户型</strong>
              <span>正在调用户型识别服务，提取房间、墙体、入口和太极点。</span>
            </div>
          ) : (
            <div>
              <strong>正在生成风水分析</strong>
              <span>正在基于已识别户型、罗盘坐向与九运宅盘生成报告。</span>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default FengShuiAnalyzer;
